<template>
    <v-list-item
        v-if="visualType === 'list'"
        id="create-new-expert"
        class="d-flex flex-row align-center"
        :disabled="!can_create_new_experts"
        @click="handleButtonClick"
    >
        <v-list-item-title class="d-flex justify-space-between">
            {{ $t('navigation.create_new_expert') }}
        </v-list-item-title>
        <template #prepend><BotIcon size="18" class="mr-2" /></template>
    </v-list-item>
    <div v-else>
        <ButtonComponent
            v-if="can_create_new_experts"
            :variant="buttonVariant"
            @click="handleButtonClick"
        >
            <BotIcon size="18" class="mr-2" />
            {{ $t('navigation.create_new_expert') }}
        </ButtonComponent>
        <div v-else>
            <ButtonComponent :variant="buttonVariant" disabled>
                <BotIcon size="18" class="mr-2" />
                {{ $t('navigation.new_expert_dialog.not_allowed_message') }}
            </ButtonComponent>
        </div>
    </div>

    <v-dialog v-model="dialogCreate" width="auto" class="dialog-create" :opacity="0.8">
        <v-card class="pa-4 rounded-lg">
            <v-btn
                icon
                size="small"
                class="close-button"
                variant="text"
                @click="dialogCreate = false"
            >
                <v-icon>mdi-close</v-icon>
            </v-btn>

            <v-card-title class="px-1 py-0">
                <h4>{{ $t('navigation.new_expert_dialog.title') }}</h4>
            </v-card-title>

            <v-card-subtitle class="px-1">
                {{ $t('navigation.create_new_expert_dialog_subtitle') }}
            </v-card-subtitle>

            <v-card-text class="px-1 py-0 mt-5">
                <v-text-field
                    ref="expertNameInput"
                    v-model="expertName"
                    counter
                    variant="outlined"
                    density="compact"
                    maxlength="60"
                    name="create-expert-name-input"
                    :placeholder="$t('navigation.new_expert_dialog.expert_name_field_title')"
                    @keydown.enter="createExpert"
                ></v-text-field>
            </v-card-text>

            <v-card-actions class="px-1">
                <div class="ml-auto">
                    <ButtonComponent
                        name="button-create-expert"
                        size="default"
                        :disabled="expertName.length === 0"
                        :loading="isCreatingNewExpert"
                        @click="createExpert"
                    >
                        {{ $t('navigation.create_new_expert_button') }}
                    </ButtonComponent>
                </div>
            </v-card-actions>
        </v-card>
    </v-dialog>
</template>

<script setup>
import { ref, computed, watch } from 'vue';
import { useRouter } from 'vue-router';
import ButtonComponent from '@/components/utils/buttons/ButtonComponent.vue';
import { useExpertStore } from '@/stores/expert';
import { useUserStore } from '@/stores/user';
import { role_values } from '@/assets/js/constants';
import { BotIcon } from 'lucide-vue-next';

const props = defineProps({
    visualType: {
        type: String,
        required: false,
        default: 'list',
    },
    buttonVariant: {
        type: String,
        required: false,
        default: 'primary',
    },
});

const router = useRouter();
const dialogCreate = ref(false);
const expertName = ref('');
const expertNameInput = ref(null);
const isCreatingNewExpert = ref(false);
const expertStore = useExpertStore();
const userStore = useUserStore();

function generateRandomName() {
    const adjectives = ['Fast', 'Quick', 'Smart', 'Wise', 'Cool', 'Neat', 'Sharp', 'Swift'];
    const nouns = ['Bot', 'AI', 'Bot', 'Bot', 'Bot', 'Bot', 'Bot', 'Bot'];
    const months = [
        'Jan',
        'Feb',
        'Mar',
        'Apr',
        'May',
        'Jun',
        'Jul',
        'Aug',
        'Sep',
        'Oct',
        'Nov',
        'Dec',
    ];
    const randomAdjective = adjectives[Math.floor(Math.random() * adjectives.length)];
    const randomNoun = nouns[Math.floor(Math.random() * nouns.length)];
    const today = new Date();
    const monthAbbr = months[today.getMonth()];
    const day = today.getDate();
    return `${randomAdjective}-${randomNoun} ${monthAbbr}${day}`;
}

const handleButtonClick = (event) => {
    console.log('handleButtonClick', event);
    if (event.ctrlKey) {
        setTimeout(() => {
            const randomName = generateRandomName();
            console.log('ctrlKey', randomName);
            expertName.value = randomName;
            expertNameInput.value = randomName;
        }, 100);
    }
    dialogCreate.value = true;
};

const can_create_new_experts = computed(() => {
    return userStore.selected_workspace_value >= role_values.editor;
});

// Focus on text field when dialog opens
watch(dialogCreate, (newValue) => {
    if (newValue) {
        expertName.value = '';
        // Use nextTick to ensure DOM is updated before focusing
        setTimeout(() => {
            expertNameInput.value?.focus();
        }, 50);
    }
});

async function createExpert() {
    isCreatingNewExpert.value = true;
    await expertStore.create_expert(expertName.value);
    isCreatingNewExpert.value = false;

    router.push(`/expert-configurator/${expertStore.expert_id}`);
    dialogCreate.value = false;
}
</script>

<style scoped lang="scss">
.dialog-create:deep(.v-overlay__content) {
    max-width: 500px;
    min-width: min(500px, 90%);
}

.v-list-item {
    :deep(.v-list-item__spacer) {
        width: 0px !important;
    }
}

.v-list-group__items .v-list-item {
    padding-inline-start: 10px !important;
}

.v-card {
    position: relative;

    .close-button {
        position: absolute;
        top: 8px;
        right: 8px;
    }
}
</style>
